<script setup lang="ts">
    import { useRoute } from 'vue-router';
    import { computed, inject, onMounted, ref } from 'vue';
    import { formatAmount, formatDate, formatDateFrench, MONTHS_FR } from '@/utils';
    import moment from 'moment';
    import UserPrelevementsMensuels from './@components/UserPrelevementsMensuels.vue';
    import UserPlanMensuel from './@components/UserPlanMensuel.vue';
import axios from 'axios';

    const route = useRoute();
    const user: any = route.meta.user;
    const souscription: any = route.meta.souscription;
    const carte: any = route.meta.carte;

    const loadingPlanMensuel = ref(false);
    const errorPlanMensuel = ref('');
    const dateDebut = ref(null as string | null);
    const dateFin = ref(null as string | null);
    const planMensuelsDatas = ref([] as { plan: any, title: string, offset: number }[]);

    onMounted(() => {
        if (user.souscription) {
            fetchPlanMensuelDatas();
        }
    })

    async function fetchPlanMensuelDatas() {
        try {
            loadingPlanMensuel.value = true;

            const responses = await Promise.all([
                axios.post(`/backoffice/users/${user.id}/plan/get_planmensuel_datas`, { souscription_id: user.souscription.id, offset: 0 }),
                axios.post(`/backoffice/users/${user.id}/plan/get_planmensuel_datas`, { souscription_id: user.souscription.id, offset: 1 }),
                axios.post(`/backoffice/users/${user.id}/plan/get_planmensuel_datas`, { souscription_id: user.souscription.id, offset: 2 }),
            ]);
            const datas = responses.filter(r => r.data).map(r => r.data);
            datas.forEach((data, index) => {
                planMensuelsDatas.value.push({
                    plan: data.plan_mensuel,
                    title: getTitlePlanMensuel(data.date_debut, data.date_fin, index),
                    offset: index
                })
            });

            /*const offsetParams = 0;
            const response = await axios.post(`/backoffice/users/${user.id}/plan/get_planmensuel_datas`, { souscription_id: user.souscription.id, offset: offsetParams });
            planMensuel.value = response.data.plan_mensuel;
            souscription.value = response.data.souscription;
            dateDebut.value = response.data.date_debut;
            dateFin.value = response.data.date_fin;*/
        } catch (err: any) {
            errorPlanMensuel.value = err;
        } finally {
            loadingPlanMensuel.value = false;
        }
    }

    function getTitlePlanMensuel(dateDebut: string, dateFin: string, offset: number) {
        const dateDebutM = moment(dateDebut);
        const dateFinM = moment(dateFin);

        return `Année ${(offset + 1)} / ${MONTHS_FR[dateDebutM.month()]} ${dateDebutM.format('YYYY')} - ${MONTHS_FR[dateFinM.month()]} ${dateFinM.format('YYYY')}`;
    }

    const quotiteUsedPercent = computed(() => {
        if (!user || !user.quotite_cessible_montant || !user.salaire || user.salaire <= 0) return 0;
        // Si on a une utilisation, on peut la calculer, sinon 0
        // Pour l'instant, on retourne 0 car on n'a pas de données d'utilisation
        return 0;
    });
</script>
<template>
    <section class="container-full-1 bg-grey-50">
        <div class="page-space-40"></div>

        <h4 class="s7 font-semibold px-3 pb-5">Membre / Plan Mensuel <span class="text-sm text-grey-500"></span></h4>
        <div class="s13 flex-right gap-3 mt--2 mb-5 px-5">
            <span class="btn shadow-200 white2">Prélèvement mensuel</span>
            <span class="btn shadow-200 light2 border-none">Données financières</span>
            <span class="btn shadow-200 light2 border-none">Plan Mensuel 1</span>
        </div>

        <!--User info - Component-->
        <div class="s20 widget flex align-items-center px-1 py-5">
            <div class="s2_mobile h-100p px-7">
                <div class="rounded-100 overflow-hidden item active"><!--active-->
                    <img :src="!!user.photo_url ? user.photo_url : '/assets/users_avatar/600w/user.jpg'" width="100%" />
                </div>
            </div>
            <div class="s4_mobile h-100p px-2">
                <h4 class="text-grey-900 mb-3" v-if="user.kyc?.id">n° CF{{ user.created_at ? moment(user.created_at).format('YYYY') : '2025' }}-{{ String(user.kyc.id).padStart(6, '0') }}</h4>
                <h4>{{ user.nom + ' ' + user.prenom }}</h4>
            </div>
            <div class="s3_mobile h-100p px-2">
                <h4 class="font-normal mb-5">{{ user.collectivite?.nom }}</h4>
                <span class="tag-state warning stroke">En cours</span>
                <span class="tag-state notif stroke">Actif</span>
            </div>
            <div class="s4_mobile h-100p px-3">
                <h4 class="font-normal">
                    Date de fin de souscription <br class="mb-2"/>
                    <b class="text-2xl">{{ souscription ? formatDateFrench(souscription.date_fin) : '-' }}</b>
                </h4>
            </div>
            <div class="s7_mobile h-100p px-5">
                <!--Barre de progression - Component-->
                <div class="h-60 rounded-100 position-relative" style="width: 100%; background-color: rgba(var(--color-black-rgb), 0.1);">
                    <div class="widget-floating py-0 text-xl flex-right">Utilisation</div>
                    <div class="h-100p flex align-items-center justify-content-left rounded-100 text-2xl px-5 bg-pop-green" :style="{'width': quotiteUsedPercent + '%'}">
                        {{ quotiteUsedPercent }}%
                    </div>
                </div>
                <!--End Barre de progression-->
            </div>
        </div>
        <!--End User info-->
        <div class="clear-both h-20"></div>


        <template v-if="souscription">
            <!--Prélèvement mensuel - Component-->
            <user-prelevements-mensuels :user-id="user.id" />

            <div class="clear-both h-5"></div>
            <div class="step vertical bg-grey-500 ml-auto mr-auto"></div>
            <div class="clear-both h-5"></div>


            <!--Données financières   - Component-->
            <div class="s20 widget px-6 pt-8 pb-2">
                <div class="s10 mt--4 ml--4 mb-5 collapse-open active">
                    <span class="px-5 py-4 bg-color-black rounded-100 text-xl">
                        <img src="/icons/icon-panel-open.svg" class="size-xs icon-neutral mr-2 collapse-icon-open" alt="" />
                        <img src="/icons/icon-panel-close.svg" class="size-xs icon-neutral mr-2 collapse-icon-close" alt="" />
                        Données financières
                    </span>
                </div>
                <div class="s10 mt--5-5 flex-right gap-3">
                    <!-- <button class="btn lg py-3 white2 active">
                        <img src="/icons/icon-consulter.svg" class="btn-icon" alt="" />
                        <span>Carte</span>
                    </button>
                    <button class="btn lg py-3 white2 active">
                        <img src="/icons/icon-consulter.svg" class="btn-icon" alt="" />
                        <span>Plan mensuel</span>
                    </button> -->
                </div>
                <div class="clear-both h-5"></div>

                    <div class="s20 items-center gap-6 px-5 pb-6 collapse-content active">

                        <div class="s20 flex gap-5">
                            <div class="s6_mobile">
                                <!--CardCarteLight-->
                                <!-- <div class="card-carte-light">
                                    <div class="card-carte-floating right-bottom">
                                        <div>
                                            <h6 class="secondary-font font-bold">Solde utilisable</h6>
                                            <div class="flex align-items-center gap-2">
                                                <h1 class="secondary-font text-5xl">25 600</h1>
                                                <h2 class="secondary-font text-3xl">F</h2>
                                                <div>
                                                    <img src="/icons/icon-eye-off.svg" class="size-medium icon-white" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <img src="/images/carte_coop.png" class="card-carte-img"/>
                                </div> -->
                                <!---->

                                <!--CardCarte-->
                                <div class="card-carte">
                                    <div class="card-carte-floating">
                                        <div class="s20" v-if="carte">
                                            <h5 class="secondary-font font-bold mb-3">Solde utilisable</h5>
                                            <div class="flex align-items-center gap-2">
                                                <h1 class="secondary-font text-6xl">{{ formatAmount(carte.solde) }}</h1>
                                                <h2 class="secondary-font text-4xl mt-2">F</h2>
                                                <div class="mt-2 ml-2">
                                                    <img src="/icons/icon-eye-off.svg" class="size-medium icon-white" />
                                                </div>
                                            </div>
                                            <div class="card-carte-floating-bottom">
                                                <h5 class="s10 secondary-font font-bold">{{ carte.numero_masque }}</h5>
                                                <div class="s10 flex-right align-items-center gap-2">
                                                    <img src="/icons/icon-edit.svg" class="size-medium icon-white" />
                                                    <h5 class="secondary-font font-bold">Commerce</h5>
                                                </div>
                                            </div>
                                            <img src="/images/carte_coop_light.png" class="card-carte-img" style="width: 100%" />
                                        </div>
                                        <div class="s20" v-else>
                                            <h5 class="secondary-font font-bold mb-3" >Aucune carte disponible</h5>
                                        </div>
                                    </div>
                                    
                                </div>
                                <!---->

                            </div>
                            <!-- <div class="flex-center">
                                <hr class="hr-vertical h-100p"/>
                            </div> -->
                            <div class="s7_mobile px-6" :class="{'s7_mobile': carte, 's10_mobile': !carte}">
                                <h4 class="font-normal text-grey-500 text-center py-3">
                                    Salaire <br class="mb-4"/>
                                    <b class="text-3xl text-grey-800">{{ formatAmount(souscription.salaire) }}</b>
                                </h4>
                                <hr/>
                                <h4 class="font-normal text-grey-500 text-center py-3">
                                    Prélèvement sur salaire % <br class="mb-4"/>
                                    <b class="text-3xl text-grey-800">{{ souscription.prelevement_pourcent }}%</b>
                                </h4>
                                <hr/>
                                <h4 class="font-normal text-grey-500 text-center py-3">
                                    Prélèvement sur salaire <br class="mb-4"/>
                                    <b class="text-3xl text-grey-800">{{ formatAmount(souscription.prelevement_montant) }}</b>
                                </h4>
                                <hr/>
                            </div>
                            <!-- <div class="flex-center">
                                <hr class="hr-vertical h-100p"/>
                            </div> -->

                            <div class="s7_mobile px-6">
                                <h4 class="font-normal text-grey-500 text-center py-3">
                                    Ans pour la Retraite<br class="mb-4"/>
                                    <b class="text-3xl text-grey-800">N/A</b>
                                </h4>
                                <hr/>
                                <h4 class="font-normal text-grey-500 text-center py-3">
                                    Quote cessible %<br class="mb-4"/>
                                    <b class="text-3xl text-grey-800">{{ souscription.quotite_cessible_pourcent }}%</b>
                                </h4>
                                <hr/>
                                <h4 class="font-normal text-grey-500 text-center py-3">
                                    Quote cessible<br class="mb-4"/>
                                    <b class="text-3xl text-grey-800">{{ formatAmount(souscription.quotite_cessible_montant) }}</b>
                                </h4>
                                <hr/>
                            </div>
                        </div>

                        <!-- <div class="clear-both h-20"></div>
                        <hr/> -->

                    </div>

                    <div class="s20 collapse-content pb-7 active">
                        <div class="s6 px-8">
                            <h4 class="font-normal text-grey-500 text-center py-3">
                                Taux Interets Microfinance<br class="mb-4"/>
                                <!-- Provenant du profil financier de la collectivité (table) -->
                                <b class="text-3xl text-grey-800">N/A</b>
                            </h4>
                            <hr/>
                            <h4 class="font-normal text-grey-500 text-center py-3">
                                Pret à Rembourser apres 3 Ans<br class="mb-4"/>
                                <b class="text-3xl text-grey-800">N/A</b>
                            </h4>
                            <hr/>
                            <h4 class="font-normal text-grey-500 text-center py-3">
                                Cash-Back TVA Attendu<br class="mb-4"/>
                                <b class="text-3xl text-grey-800">N/A</b>
                            </h4>
                        </div>
                        <div class="s7 px-8">
                            <h4 class="font-normal text-grey-500 text-center py-3">
                                Prêt en Concession<br class="mb-4"/>
                                <b class="text-3xl text-grey-800">{{ formatAmount(souscription.pret_concession) }}</b>
                            </h4>
                            <hr/>
                            <h4 class="font-normal text-grey-500 text-center py-3">
                                Prélèvement à la Solde<br class="mb-4"/>
                                <b class="text-3xl text-grey-800">{{ formatAmount(souscription.prelevement_solde) }}</b>
                            </h4>
                            <hr/>
                            <h4 class="font-normal text-grey-500 text-center py-3">
                                Pret à Rembourser<br class="mb-4"/>
                                <b class="text-3xl text-grey-800">{{ formatAmount(souscription.pret_rembourser) }}</b>
                            </h4>
                            <hr/>
                        </div>
                        <div class="s7 px-8">
                            <h4 class="font-normal text-grey-500 text-center py-3">
                                Pouvoir Achat 1 An<br class="mb-4"/>
                                <b class="text-3xl text-grey-800">{{ formatAmount(souscription.pouvoir_achat_an) }}</b>
                            </h4>
                            <hr/>
                            <h4 class="font-normal text-grey-500 text-center py-3">
                                Pouvoir Achat 3 Ans<br class="mb-4"/>
                                <b class="text-3xl text-grey-800">{{ formatAmount(souscription.pouvoir_achat) }}</b>
                            </h4>
                            <hr/>
                            <h4 class="font-normal text-grey-500 text-center py-3">
                                Durée Financement <br class="mb-4"/>
                                <b class="text-3xl text-grey-800">{{ souscription.duree_mois }} Mois</b> - <b class="text-3xl text-grey-800">{{ parseInt((parseInt(souscription.duree_mois) / 12).toString()) }} ans</b>
                            </h4>
                        </div>
                    </div>

            </div>
         </template>

        <div class="clear-both h-5"></div>
        <div class="step vertical bg-grey-500 ml-auto mr-auto"></div>
        <div class="clear-both h-5"></div>

        <!----->
        <!--*** Plan mensuel  Widget *** -->
        <template v-if="user.souscription">
            <template v-for="(plan_mensuel, index) in planMensuelsDatas" :key="index">
                <user-plan-mensuel :plan-mensuel="plan_mensuel.plan" :title="plan_mensuel.title" />
                <template v-if="index + 1 < planMensuelsDatas.length">
                    <div class="clear-both h-5"></div>
                    <div class="step vertical bg-grey-500 ml-auto mr-auto"></div>
                    <div class="clear-both h-5"></div>
                </template>
            </template>
        </template>
        <div class="clear-both h-40"></div>

    </section>
</template>